import { useEffect, useState } from "react";
import { api, getVisitorId } from "../lib/api";
import { toast } from "sonner";
import { Plus, Check, MapPin, Send } from "lucide-react";
import useSeo from "../hooks/useSeo";

const CITIES = ["Hyderabad", "Bangalore", "Pune"];

export default function Propose() {
  useSeo({
    title: "Propose an event — saamne",
    description:
      "Tell saamne what you'd love to be invited to in Hyderabad, Bangalore or Pune. Coffee circles, walks, watch parties, supper clubs, lifeskill workshops — we read every proposal personally.",
    path: "/propose",
  });
  const [proposals, setProposals] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "Hyderabad", title: "", description: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => api.get("/wishes").then((r) => setProposals(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name.trim() || !form.phone.trim() || !form.title.trim()) {
      toast.error("Name, phone, and an event idea — at minimum.");
      return;
    }
    setSending(true);
    try {
      await api.post("/wishes", {
        title: form.title.trim(),
        description: form.description.trim() || form.title.trim(),
        category: "other",
        city: form.city,
        suggested_by: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        visitor_id: getVisitorId(),
      });
      setDone(true);
      toast.success("Got it. We'll reach out if it sparks.");
      load();
    } catch { toast.error("Couldn't save. Try again."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="propose-page" className="max-w-5xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-24">
      <div className="label-eyebrow">For the next six months</div>
      <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight">
        Suggest a <em style={{ color: "var(--highlight)" }}>gathering</em>.
      </h1>
      <p className="mt-5 max-w-2xl text-lg" style={{ color: "var(--text-muted)" }}>
        A coffee tasting, a walk, a film evening, anything in between — tell us what you'd love to see us host in Hyderabad, Bangalore or Pune.
      </p>

      <section className="mt-10 max-w-2xl space-y-4 text-base leading-relaxed" style={{ color: "var(--text-main)" }}>
        <h2 className="font-serif text-2xl md:text-3xl leading-tight">What makes a good proposal</h2>
        <p>
          The best ideas are usually the simplest. A specific place. A specific
          time of day. A theme small enough that twelve strangers can wrap
          their arms around it. A monsoon poetry reading on a rooftop. A
          breakfast crawl through old Bangalore. A silent reading hour. A
          South-Indian filter-coffee circle. A film by a director nobody under
          thirty has heard of. A walk through a lake at sunrise.
        </p>
        <p>
          You don't have to host it yourself — though you're welcome to. If
          your idea sparks something, we will reach out, find a host, scout a
          venue, and send invites to the people who said yes. We host the
          best-loved proposals first. The rest go into a slow queue and are
          revisited every quarter.
        </p>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Tip: be specific about the city and the time of year. "A picnic" is
          harder to host than "a Cubbon Park breakfast in March."
        </p>
      </section>

      <div className="grid lg:grid-cols-12 gap-10 mt-12">
        <div className="lg:col-span-5">
          {done ? (
            <div className="card-warm p-8 text-center" data-testid="propose-success">
              <div className="mx-auto mb-4 flex items-center justify-center rounded-full"
                   style={{ width: 56, height: 56, background: "var(--surface)", color: "var(--primary)" }}>
                <Check size={28} />
              </div>
              <h3 className="font-serif text-2xl">Idea received.</h3>
              <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
                We read every proposal personally. If it sparks something, we'll call.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="propose-form" className="card-warm p-7 space-y-4">
              <div className="label-eyebrow">Your proposal</div>
              <input placeholder="Event idea — a one-liner" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="propose-title" />
              <textarea rows={4} placeholder="A line or two — what would make this special?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="propose-comment" />
              <div className="grid grid-cols-3 gap-2" data-testid="propose-city-picker">
                {CITIES.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, city: c })}
                          data-active={form.city === c} data-testid={`propose-city-${c.toLowerCase()}`}
                          className="chip">{c}</button>
                ))}
              </div>
              <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="propose-name" />
              <input type="tel" placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="propose-phone" />
              <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="propose-email" />
              <button type="submit" disabled={sending} className="btn-primary w-full justify-center" data-testid="propose-submit">
                <Send size={16} /> {sending ? "Sending…" : "Propose this event"}
              </button>
            </form>
          )}
        </div>
        <div className="lg:col-span-7">
          <div className="label-eyebrow mb-4">Already proposed by the community</div>
          <div className="space-y-3">
            {proposals.length === 0 && <p className="text-sm" style={{ color: "var(--text-muted)" }}>Be the first.</p>}
            {proposals.map((p) => (
              <div key={p.id} data-testid={`proposal-${p.id}`} className="card-warm p-5">
                <div className="flex items-baseline gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
                  <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.city}</span>
                  {p.suggested_by && <span>· by {p.suggested_by}</span>}
                </div>
                <h3 className="font-serif text-xl mt-2 leading-snug">{p.title}</h3>
                {p.description && p.description !== p.title && (
                  <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>{p.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
